import { isPresent } from "@/lib/utils"
import vuexStore from "@/store"
import router from "@/router"
import i18n from "@/store/i18n"
import ApiClient from "@/lib/api-client"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { formatAmount } from "@/lib/checkout-utils"
import { pageDataToProps } from "@shared/page-data"
import { Product } from "@shared/types"
import { toDate } from "@/lib/date-utils"
import { getCachedPageByPath } from "@/lib/page-cache"
import fetchPageData from "@/lib/fetch-page-data"
import {
  Row,
  QueryOptions,
  DeleteQuery,
  TrackEventPayload,
  MvtRoute,
  NavigateOptions,
  Occurrence,
  ScheduleQueryOptions,
  ProductDetails,
  PurchaseResult,
  Page,
  PagesQueryOptions,
  ProgressStatus,
  StartOptions,
  DateInput
} from "./types"
import { EventManager, MVT_EVENTS } from "./event-manager"
import { NativeBridge } from "./native-bridge"

export { MVT_EVENTS } from "./event-manager"

// On 422 validation failure, caught errors are augmented with `fieldErrors`:
//
//   try { await mvt.tables.meals.create(value) }
//   catch (err) {
//     if (err.fieldErrors) {
//       // { calories: ["must be a number"], name: ["is required"] }
//     } else throw err
//   }
//
// Phrasing is sourced from `lib.validation.*` in the locale files.

type ValidationCode = { code: string; expected?: any }

type ApiError = {
  response?: { status?: number; data?: Record<string, unknown> }
  fieldErrors?: Record<string, string[]>
}

function renderCode({ code, expected }: ValidationCode): string {
  const value = Array.isArray(expected)
    ? expected.join(", ")
    : String(expected ?? "")
  const key = `lib.validation.${code}`
  const finalKey = i18n.te(key) ? key : "lib.validation.unknown"
  return i18n.t(finalKey, { expected: value }) as string
}

function attachFieldErrors(err: any): never {
  const data = err?.response?.data
  if (data?.reason === "validation_error" && data.errors) {
    err.fieldErrors = Object.fromEntries(
      Object.entries<ValidationCode[]>(data.errors).map(([field, list]) => [
        field,
        list.map(renderCode)
      ])
    )
  }
  throw err
}

class ApiService {
  static async post(endpoint: string, body: Record<string, any>) {
    try {
      const response = await ApiClient.post(endpoint, body)
      return response.data
    } catch (err) {
      attachFieldErrors(err)
    }
  }

  static async get(endpoint: string, params: Record<string, any>) {
    const response = await ApiClient.get(endpoint, { params })
    return response.data
  }

  static async put(endpoint: string, body: Record<string, any>) {
    try {
      const response = await ApiClient.put(endpoint, body)
      return response.data
    } catch (err) {
      attachFieldErrors(err)
    }
  }

  static async delete(endpoint: string, params: Record<string, any>) {
    try {
      const response = await ApiClient.delete(endpoint, { params })
      return response.data
    } catch (err) {
      attachFieldErrors(err)
    }
  }
}

class Table {
  constructor(private readonly slug: string) {}

  private get endpoint() {
    return `/tables/${this.slug}`
  }

  async create(input: any | Row[], created_at?: Date) {
    const data = Array.isArray(input) ? input : { value: input, created_at }
    return await ApiService.post(this.endpoint, { data })
  }

  async update(id: string, value: any) {
    return await ApiService.put(this.endpoint, { id, value })
  }

  async delete(query?: DeleteQuery) {
    const params: DeleteQuery = {}

    if (query?.id) params.id = query.id
    if (query?.where) params.where = query.where

    return await ApiService.delete(this.endpoint, params)
  }

  async query(options: QueryOptions = {}) {
    const { all_members, ...query } = options
    try {
      return await ApiService.get(this.endpoint, {
        query,
        ...(all_members ? { all_members: true } : {})
      })
    } catch (err) {
      if ((err as ApiError).response?.status === 404) return { results: [] }
      throw err
    }
  }

  async first(options: QueryOptions = {}) {
    const res = await this.query({ ...options, limit: 1 })
    return res.results[0] ?? null
  }
}

const eventManager = new EventManager()

let purchaseSeq = 0

class ProductsApi {
  // Member-scoped details (price/availability can vary per member), so the
  // cache is dropped on logout and refetched lazily per id on first read.
  private readonly cache = new Map<number, ProductDetails>()

  constructor() {
    eventManager.on(MVT_EVENTS.LOGGED_OUT, () => this.cache.clear())
  }

  hasPurchased(id: number): boolean {
    return vuexStore.getters["auth/hasPurchasedProduct"](id)
  }

  async getDetails(id: number): Promise<ProductDetails> {
    const cached = this.cache.get(id)
    if (cached) return cached

    const product: Product = await vuexStore.dispatch(
      "products/fetchProduct",
      id
    )
    const details = this.toDetails(product)
    this.cache.set(id, details)
    return details
  }

  async purchase(id: number): Promise<PurchaseResult> {
    if (this.hasPurchased(id)) return { status: "paid" }

    const token = `mvt-purchase-${++purchaseSeq}`

    return new Promise<PurchaseResult>(resolve => {
      let settled = false

      const settle = (result: PurchaseResult) => {
        if (settled) return
        settled = true
        EventBus.$off(EVENTS.CHECKOUT_COMPLETED, onCompleted)
        EventBus.$off(EVENTS.CHECKOUT_CLOSED, onClosed)
        resolve(result)
      }

      // The token identifies this invocation so a stale close from a prior
      // checkout of the same product (the kept-alive Checkout deactivating as
      // the next one opens) cannot settle it. On close, ownership decides the
      // outcome: some checkout exits grant ownership without a `completed`
      // event (e.g. an already-active purchase confirmed server-side), so
      // ownership-now-true on close is a success.
      const onCompleted = (_id: number, eventToken?: string) => {
        if (eventToken !== token) return
        settle({ status: "paid" })
      }
      const onClosed = (_id: number, eventToken?: string) => {
        if (eventToken !== token) return
        settle({ status: this.hasPurchased(id) ? "paid" : "cancelled" })
      }

      EventBus.$on(EVENTS.CHECKOUT_COMPLETED, onCompleted)
      EventBus.$on(EVENTS.CHECKOUT_CLOSED, onClosed)

      EventBus.$emit(EVENTS.TRIGGER_CHECKOUT, { productIds: [id], token })
    })
  }

  private toDetails(product: Product): ProductDetails {
    return {
      id: product.id,
      name: product.title,
      priceLabel: this.priceLabel(product)
    }
  }

  private priceLabel(product: Product): string {
    if (!isPresent(product.price)) return ""

    const symbol = product.symbol || "$"
    const currency = product.currency
      ? ` ${product.currency.toUpperCase()}`
      : ""
    return `${symbol}${formatAmount(product.price)}${currency}`
  }
}

const loggedIn = (): boolean => isPresent(vuexStore.getters["auth/currentUser"])

const codedError = (code: string, message: string): Error => {
  const error: any = new Error(message)
  error.code = code
  return error
}

const notAuthenticated = (): Error =>
  codedError("not_authenticated", "Not signed in")

// Resolves a member-facing path to its page uuid. The host already caches every
// visited page by path (IndexedDB + the inlined FIT_initialState), so a synced
// path lookup is available without a round-trip; misses hydrate in the
// background and re-resolve once the cache fills.
class Progress {
  private readonly uuidByPath: Record<string, string> = {}
  private readonly hydrating = new Set<string>()
  private readonly progressHydrated = new Set<string>()
  private activeContent: Array<Page & { id: string }> = []
  private activeHydrated = false
  private activeFetchSeq = 0

  constructor() {
    const invalidate = () => {
      this.progressHydrated.clear()
      this.activeHydrated = false
      this.activeFetchSeq++
      this.activeContent = []
    }
    eventManager.on(MVT_EVENTS.LOGGED_IN, invalidate)
    eventManager.on(MVT_EVENTS.LOGGED_OUT, invalidate)
    eventManager.on(MVT_EVENTS.PROGRESS_CHANGED, mutation => {
      if (mutation?.type === "pages/setPageSubscription") {
        this.activeHydrated = false
      } else if (mutation?.type === "pages/deletePageSubscription") {
        this.removeFromActive(mutation.payload)
      }
    })
  }

  status(path: string): ProgressStatus {
    const uuid = this.resolveUuid(path)

    if (uuid) this.hydrateProgress(path, uuid)

    // getPageProgress is not auth-scoped and the store slice survives a logout,
    // so a logged-out (or not-yet-refetched next-member) read would otherwise
    // surface the previous member's row. Gate it on the session.
    const progress =
      uuid && loggedIn() ? vuexStore.getters["pages/getPageProgress"](uuid) : {}
    const completed = uuid
      ? vuexStore.getters["pages/isPageComplete"](uuid)
      : false

    return {
      started: isPresent(progress.started_at),
      startedAt: progress.started_at ?? null,
      completed,
      completedAt: progress.completed_at ?? null,
      percent: progress.percent_complete ?? 0
    }
  }

  get active(): Page[] {
    if (!loggedIn()) return []

    this.hydrateActive()

    return this.activeContent
      .filter(item => !this.isComplete(item.id))
      .map(item => ({
        path: item.path,
        title: item.title ?? "",
        type: item.type
      }))
      .filter((page: Page) => isPresent(page.path))
  }

  async start(path: string, options: StartOptions = {}): Promise<void> {
    if (!loggedIn()) throw notAuthenticated()

    const startDate = isPresent(options.startedAt)
      ? this.toIsoDate(options.startedAt)
      : undefined

    const pageId = await this.resolveUuidAsync(path)
    await vuexStore.dispatch("pages/subscribe", { pageId, startDate })
    await this.refetchActive()
  }

  async complete(path: string): Promise<void> {
    if (!loggedIn()) throw notAuthenticated()

    const pageId = await this.resolveUuidAsync(path)
    await vuexStore.dispatch("pages/completePage", pageId)
    // completePage records the completion; only this refetch carries completed_at
    // + percent. status() latches its own fetch per path, so drop that latch
    // first — otherwise an already-hydrated page keeps status() reading the
    // pre-completion row. Its failure must not reject a completion that
    // succeeded, so it stays best-effort.
    this.progressHydrated.delete(path)
    await vuexStore
      .dispatch("pages/fetchProgress", pageId)
      .catch(() => undefined)
  }

  /**
   * Destructive: drops the member's enrolment and every progress record beneath
   * this page (the whole subtree), back to never-started. This is NOT a
   * per-page un-complete — there is no way to un-tick a single page.
   */
  async reset(path: string): Promise<void> {
    if (!loggedIn()) throw notAuthenticated()

    const pageId = await this.resolveUuidAsync(path)
    await vuexStore.dispatch("pages/unsubscribe", pageId)
    this.removeFromActive(pageId)
  }

  private toIsoDate(value: DateInput): string {
    const date = new Date(value)
    if (isNaN(date.getTime())) {
      throw codedError("invalid_date", `Invalid startedAt: ${String(value)}`)
    }
    return date.toISOString()
  }

  private isComplete(uuid?: string): boolean {
    return isPresent(uuid)
      ? vuexStore.getters["pages/isPageComplete"](uuid)
      : false
  }

  private hydrateActive(): void {
    if (this.activeHydrated) return
    void this.fetchActive()
  }

  private async refetchActive(): Promise<void> {
    await this.fetchActive()
  }

  private async fetchActive(): Promise<void> {
    this.activeHydrated = true
    const seq = ++this.activeFetchSeq

    try {
      const { active } = await ApiService.get("/progress/active", {})
      if (seq === this.activeFetchSeq && loggedIn()) {
        this.activeContent = Array.isArray(active) ? active : []
        eventManager.emit(MVT_EVENTS.PROGRESS_CHANGED)
      }
    } catch {
      if (seq === this.activeFetchSeq) this.activeHydrated = false
    }
  }

  private removeFromActive(pageId?: string): void {
    if (!isPresent(pageId)) return
    this.activeContent = this.activeContent.filter(item => item.id !== pageId)
  }

  private knownUuid(path: string): string | null {
    if (this.uuidByPath[path]) return this.uuidByPath[path]

    const initialState = (window as any).FIT_initialState
    const inlined = initialState?.[path]?.props?.page_id
    return inlined ? (this.uuidByPath[path] = inlined) : null
  }

  private resolveUuid(path: string): string | null {
    const known = this.knownUuid(path)
    if (known) return known

    this.hydrate(path)
    return null
  }

  private async resolveUuidAsync(path: string): Promise<string> {
    const uuid = this.knownUuid(path) ?? (await this.lookup(path))
    if (!uuid) throw codedError("not_found", `Unknown page: ${path}`)
    return uuid
  }

  // Resolves an unknown path, then loads its progress so a first off-page read
  // fills in (and re-renders via progress:changed) instead of staying a
  // false zero.
  private hydrate(path: string): void {
    if (this.hydrating.has(path)) return
    this.hydrating.add(path)

    this.lookup(path)
      .then(uuid => {
        if (uuid && loggedIn()) {
          return vuexStore.dispatch("pages/fetchProgress", uuid)
        }
      })
      .catch(() => undefined)
      .finally(() => this.hydrating.delete(path))
  }

  // status() resolves the uuid synchronously (inlined/cached) but the progress
  // row may not be loaded — fetch it once so percent/completed_at aren't a
  // false zero for a page the member never navigated to.
  private hydrateProgress(path: string, uuid: string): void {
    if (this.progressHydrated.has(path) || !loggedIn()) return
    this.progressHydrated.add(path)

    vuexStore.dispatch("pages/fetchProgress", uuid).catch(() => {
      this.progressHydrated.delete(path)
    })
  }

  private async lookup(path: string): Promise<string | null> {
    const cached = await getCachedPageByPath(path)
    let uuid = cached?.props?.page_id

    if (!uuid) {
      const fetched = await fetchPageData(path).catch(() => null)
      uuid = fetched?.props?.page_id
    }

    if (uuid) this.uuidByPath[path] = uuid
    return uuid ?? null
  }
}

type RawScheduledItem = {
  id: string | number
  starts_at: string
  ends_at: string
  is_all_day: boolean
  item?: {
    type?: string
    content_type?: string
    data?: { path?: string; title?: string }
  } | null
}

// Only path-addressable pages surface as occurrences; legacy scheduled content
// (events, forms, tasks) carries no `data.path` and is intentionally omitted.
function toOccurrence(raw: RawScheduledItem): Occurrence | null {
  const page = raw.item
  if (!page?.data?.path) return null

  return {
    id: raw.id,
    startsAt: raw.starts_at,
    endsAt: raw.ends_at,
    isAllDay: !!raw.is_all_day,
    path: page.data.path,
    title: page.data.title ?? "",
    type: page.content_type ?? page.type ?? "page"
  }
}

class Schedule {
  async query({
    from,
    to,
    timezone,
    type
  }: ScheduleQueryOptions): Promise<Occurrence[]> {
    const filters = Array.isArray(type) ? type : type ? [type] : []
    const types = filters.length ? filters : [undefined]

    // from/to are whole calendar days; timezone is forwarded only when given so
    // the server applies its own member/account fallback otherwise.
    const range = {
      from: toDate(from, "YYYY-MM-DD"),
      to: toDate(to, "YYYY-MM-DD"),
      ...(timezone ? { timezone } : {})
    }

    const batches = await Promise.all(
      types.map(filter =>
        this.fetchAll({ ...range, ...(filter ? { filter } : {}) })
      )
    )

    const seen = new Set<string | number>()
    const occurrences: Occurrence[] = []

    for (const raw of batches.flat()) {
      const occurrence = toOccurrence(raw)
      if (occurrence && !seen.has(occurrence.id)) {
        seen.add(occurrence.id)
        occurrences.push(occurrence)
      }
    }

    return occurrences
  }

  private async fetchAll(
    params: Record<string, any>
  ): Promise<RawScheduledItem[]> {
    const items: RawScheduledItem[] = []
    let page = 1
    let totalPages = 1

    do {
      try {
        const data = await ApiService.get("/schedule", { ...params, page })
        if (data?.items) items.push(...data.items)
        totalPages = data?.pagination?.totalPages ?? 1
      } catch (err) {
        if ((err as ApiError).response?.status === 404) break
        throw err
      }
      page += 1
    } while (page <= totalPages)

    return items
  }
}

// $mvt.pages exposes a clean calendar vocabulary; the content engine keeps the
// fixed collection block's names. This map is that translation, done client-side.
const PAGES_CALENDAR_PARAM: Record<string, string> = {
  any: "any_schedule",
  global: "global_schedule",
  member: "member_schedule",
  own: "own_schedule"
}

const TIMEZONE = { app: "app", user: "user" } as const

const WITHIN_UNIT_SECONDS: Record<string, number> = {
  h: 3600,
  d: 86400,
  w: 604800
}

const MAX_PAGE_SIZE = 20
const DEFAULT_ORDER = "created_desc"

function withinSeconds(within: string): number {
  const match = /^(\d+)([hdw])$/.exec(within)
  if (!match) {
    throw codedError(
      "invalid_duration",
      `schedule.within expects "<n>h" | "<n>d" | "<n>w", got: ${within}`
    )
  }

  return Number(match[1]) * WITHIN_UNIT_SECONDS[match[2]]
}

type RawContentItem = {
  type?: string
  content_type?: string
  access?: Record<string, any> | null
  current_schedule?: {
    id: string | number
    starts_at: string
    ends_at: string
    is_all_day: boolean
  } | null
  data?: Record<string, any> | null
}

// Page-backed content always carries a path; only legacy-model types (which
// this surface doesn't cover) can be path-less, and those are dropped.
function toPage(raw: RawContentItem): Page | null {
  const data = raw.data || {}
  if (!isPresent(data.path)) return null

  const page: Page = {
    path: data.path,
    title: data.title ?? "",
    type: raw.content_type ?? raw.type ?? "page",
    image: data.banner ?? null,
    video: data.video ?? null,
    product: data.product ?? null,
    tags: data.tags ?? [],
    access: raw.access ?? null
  }

  if (raw.current_schedule) {
    page.schedule = {
      id: raw.current_schedule.id,
      startsAt: raw.current_schedule.starts_at,
      endsAt: raw.current_schedule.ends_at,
      isAllDay: !!raw.current_schedule.is_all_day
    }
  }

  if (data.props) page.props = data.props

  return page
}

// The content-plane sibling of $mvt.tables: queries the member-navigable page
// space through the content engine (GET /content/:type), so results are
// access-scoped pages with paths — never raw rows.
class Pages {
  async query(options: PagesQueryOptions = {}): Promise<{ results: Page[] }> {
    const type = options.type ?? "any"

    try {
      const data = await ApiService.get(
        `/content/${encodeURIComponent(type)}`,
        this.toParams(options)
      )
      const items: RawContentItem[] = data?.items ?? []

      return {
        results: items.map(toPage).filter((page): page is Page => page !== null)
      }
    } catch (err) {
      const response = (err as ApiError).response
      const body = response?.data as
        | { reason?: string; message?: string }
        | undefined

      if (response?.status === 422 && body?.reason === "invalid_query") {
        throw codedError("invalid_query", body.message ?? "Invalid query")
      }
      if (response?.status === 404) return { results: [] }
      throw err
    }
  }

  private toParams(options: PagesQueryOptions): Record<string, any> {
    // The context page is explicit (`of`); passing it alone means "children".
    const scope = options.scope ?? (options.of ? "children" : "app")

    // `sdk: 1` marks the strict path (loud validation, no global content-type
    // fallbacks) so the engine never infers it from the payload.
    const params: Record<string, any> = {
      sdk: 1,
      scope,
      order: options.order ?? DEFAULT_ORDER
    }

    if (options.of) params.of = options.of
    if (options.tags?.length) {
      params.with_tags = options.tags
      params.with_all_tags = options.match === "all"
    }
    if (options.where) params.where = options.where
    if (options.select?.length) params.select = options.select

    const schedule = options.schedule
    if (schedule) {
      params.direction = schedule.when
      if (schedule.within) params.duration = withinSeconds(schedule.within)
      if (schedule.calendar) {
        params.schedule_type =
          PAGES_CALENDAR_PARAM[schedule.calendar] ?? schedule.calendar
      }
      if (schedule.includeCurrent !== undefined) {
        params.include_current = schedule.includeCurrent
      }
      const timezone = this.resolveTimezone(schedule.timezone)
      if (timezone) params.timezone = timezone
    }

    // The 20/page ceiling is the server's; paging rides limit + offset.
    const limit = Math.min(options.limit ?? MAX_PAGE_SIZE, MAX_PAGE_SIZE)
    params.limit = limit
    params.offset = limit * (Math.max(options.page ?? 1, 1) - 1)

    return params
  }

  // "user" resolves to the member's device timezone; "app" (the default) omits
  // the param so the server falls back to the account timezone.
  private resolveTimezone(timezone?: string): string | undefined {
    if (!timezone || timezone === TIMEZONE.app) return undefined
    if (timezone === TIMEZONE.user) {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    }

    return timezone
  }
}

class Sdk {
  readonly MVT_EVENTS = MVT_EVENTS
  readonly tables: Record<string, Table>
  readonly pages: Pages
  readonly schedule: Schedule
  readonly native: NativeBridge
  readonly products: ProductsApi
  readonly progress: Progress
  readonly userProperties: Record<string, unknown>
  private readonly stopRouteWatch: () => void

  constructor() {
    this.pages = new Pages()
    this.schedule = new Schedule()
    this.native = new NativeBridge()
    this.progress = new Progress()

    const cache: Record<string, Table> = {}
    this.tables = new Proxy(cache, {
      get(target, slug) {
        if (typeof slug !== "string") return undefined
        return target[slug] || (target[slug] = new Table(slug))
      }
    })

    this.products = new ProductsApi()

    const submittedProperties = (): Record<string, unknown> =>
      isPresent(vuexStore.getters["auth/currentUser"])
        ? vuexStore.getters[
            "subscriberProperties/subscriberSubmittedProperties"
          ]
        : {}

    this.userProperties = new Proxy(
      {},
      {
        get: (_target, key) => {
          if (typeof key !== "string") return undefined
          if (!isPresent(vuexStore.getters["auth/currentUser"])) return null

          return submittedProperties()[key] ?? null
        },
        has: (_target, key) =>
          typeof key === "string" && key in submittedProperties(),
        ownKeys: () => Reflect.ownKeys(submittedProperties()),
        getOwnPropertyDescriptor: (_target, key) =>
          typeof key === "string" && key in submittedProperties()
            ? {
                value: submittedProperties()[key],
                enumerable: true,
                configurable: true
              }
            : undefined,
        set: (_target, key, value) => {
          if (typeof key !== "string") return false
          if (!isPresent(vuexStore.getters["auth/currentUser"])) return true

          vuexStore
            .dispatch("subscriberProperties/setPropertyOptimistically", {
              slug: key,
              value
            })
            .catch((error: unknown) => {
              eventManager.emit(MVT_EVENTS.USER_PROPERTIES_ERROR, {
                key,
                value,
                error
              })
            })

          return true
        }
      }
    )

    const stopRouteWatch = router.afterEach(() =>
      eventManager.emit(MVT_EVENTS.ROUTE_CHANGED)
    )
    this.stopRouteWatch = () => stopRouteWatch()
  }

  destroy(): void {
    this.stopRouteWatch()
    this.native.destroy()
  }

  get events() {
    return eventManager
  }

  trackEvent(eventName: string, payload: TrackEventPayload = {}): void {
    const enrichedPayload: TrackEventPayload = {
      timestamp: Date.now(),
      ...payload
    }

    if (!("user" in enrichedPayload)) {
      enrichedPayload.user = this.user
    }

    eventManager.emit(eventName, enrichedPayload)
  }

  get components() {
    return componentsRegistry
  }

  get user() {
    const user = vuexStore.getters["auth/currentUser"]

    return isPresent(user)
      ? {
          name: user.name,
          email: user.email,
          id: user.subscriberUuid
        }
      : null
  }

  get page() {
    const currentPage = (vuexStore as any).state?.pages?.currentPage || {}
    const pageData = currentPage.id
      ? vuexStore.getters["pages/getPageData"](currentPage.id) || {}
      : {}

    return {
      props: Object.freeze(
        pageDataToProps({
          pageData,
          builtInFieldContext: {
            pageTitle: currentPage.title,
            currentSchedule: currentPage.currentSchedule
          }
        })
      )
    }
  }

  async logout() {
    await vuexStore.dispatch("auth/logout")
  }

  async login() {
    router.push("#auth")
  }

  navigate(path: string, options: NavigateOptions = {}): void {
    if (!path.startsWith("/") || path.startsWith("//")) {
      throw new Error(
        `$mvt.navigate expects an in-app path starting with "/", got: ${path}`
      )
    }

    const navigation = options.replace
      ? router.replace(path)
      : router.push(path)

    // Swallow redirected/duplicate navigation rejections (e.g. navigating to
    // the current path) so authored code never sees an unhandled rejection.
    navigation.catch(() => undefined)
  }

  get route(): MvtRoute {
    const { path, params, query } = router.currentRoute

    return { path, params, query }
  }
}

type ComponentEntry = {
  props: Record<string, any>
  pageProps: Record<string, any>
  app: any
  dispose?: () => void
}

class ComponentsRegistry {
  private readonly components: Record<string, ComponentEntry> = {}

  register(
    id: string,
    {
      props,
      pageProps = {},
      app,
      dispose
    }: {
      props: Record<string, any>
      pageProps?: Record<string, any>
      app: any
      dispose?: () => void
    }
  ) {
    this.components[id] = { props, pageProps, app, dispose }
  }

  unregister(id: string) {
    this.components[id]?.dispose?.()
    delete this.components[id]
  }

  private entry(id: string): ComponentEntry {
    if (!this.components[id]) {
      this.components[id] = { props: {}, pageProps: {}, app: null }
    }

    return this.components[id]
  }

  setProps(id: string, props: Record<string, any>) {
    Object.assign(this.entry(id).props, props)

    eventManager.emit(`${MVT_EVENTS.PROPS_UPDATED}:${id}`, props)
  }

  getProps(id: string) {
    return this.components[id]?.props || {}
  }

  setPageProps(id: string, pageProps: Record<string, any>) {
    Object.assign(this.entry(id).pageProps, pageProps)
  }

  getPageProps(id: string) {
    return this.components[id]?.pageProps || {}
  }

  get(id: string) {
    return this.components[id]
  }
}

const componentsRegistry = new ComponentsRegistry()

export default Sdk
